import api from '@creativegs/pipe-toolkit/utils/api'

import { getPrimerPaymentId, PAYMENT_HOST } from 'utils/paymentHelper'
import {
  getAccessTokenFromSession,
  getOrderIdFromSession,
} from 'utils/sessionHelper'
import {
  Utms,
  UpdateValues,
  Order,
  SignupUserApi,
  ApiResponse,
  RequestBody,
  CheckoutApi,
  SignupErrors,
  CustomData,
} from 'types/order'
import { MetaData, PrimerSession } from 'types/primer'

const getAuthentificatedHeaders = (): { [key: string]: string } => {
  return {
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_KEY}`,
    'X-Order-Access-Token': getAccessTokenFromSession(),
  }
}

export const getOrder = async (): Promise<Order | null> => {
  try {
    const response = await api.get<ApiResponse<Order>>(
      `/api/orders/${getOrderIdFromSession()}`,
      { headers: getAuthentificatedHeaders() }
    )

    return response.data
  } catch (e) {
    return null
  }
}

export const createOrder = async (
  pipelineId: number,
  coloring: Utms,
  customData?: CustomData
): Promise<Order | null> => {
  try {
    const body: {
      pipeline_id: number
      coloring: Utms
      custom_data?: CustomData
    } = {
      pipeline_id: pipelineId,
      coloring,
    }

    if (customData) {
      body.custom_data = customData
    }

    const response = await api.post<RequestBody, ApiResponse<Order>>(
      '/api/orders',
      body,
      {
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_KEY}`,
        },
      }
    )

    const { data } = response

    return data
  } catch {
    return null
  }
}

export const updateOrder = async (
  orderId: number,
  updateOrderValues: UpdateValues
): Promise<boolean> => {
  try {
    await api.put(`/api/orders/${orderId}`, updateOrderValues, {
      headers: getAuthentificatedHeaders(),
    })

    return true
  } catch (e) {
    return false
  }
}

const isEmailError = (errors: { [key: string]: string[] }) =>
  Object.entries(errors).find((error) => error.includes('email'))

export const signupUser = async (
  email: string,
  marketingAccepted: boolean
): Promise<SignupUserApi | SignupErrors> => {
  try {
    const body = {
      email,
      eligible_for_marketing: marketingAccepted,
    }

    const response = await api.post<RequestBody, ApiResponse<SignupUserApi>>(
      `/api/orders/${getOrderIdFromSession()}/signup`,
      body,
      {
        headers: getAuthentificatedHeaders(),
      }
    )

    return response.data
  } catch (error) {
    if (error && error.errors && isEmailError(error.errors)) {
      return SignupErrors.email
    }

    return SignupErrors.default
  }
}

export const checkoutOrder = async (): Promise<CheckoutApi | null> => {
  try {
    const response = await api.post<RequestBody, CheckoutApi>(
      `/api/orders/${getOrderIdFromSession()}/checkout`,
      {},
      { headers: getAuthentificatedHeaders() }
    )

    return response
  } catch {
    return null
  }
}

export const acceptOrderChance = async (): Promise<void | null> => {
  try {
    await api.post(
      `/api/orders/${getOrderIdFromSession()}/use-chance`,
      {},
      { headers: getAuthentificatedHeaders() }
    )
  } catch {
    return null
  }
}

export const createPrimerSessionToken = async (
  userId: number,
  offerId: number,
  userAccessToken: string,
  metaData: MetaData
): Promise<PrimerSession | false> => {
  try {
    const paymodeId = getPrimerPaymentId()
    const request = await fetch(
      `${PAYMENT_HOST}/pay/checkout/${offerId}/${userId}/${paymodeId}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: userAccessToken,
        },
        method: 'POST',
        body: JSON.stringify(metaData),
      }
    )
    const response = request.json()

    return response
  } catch (e) {
    return false
  }
}
